import { getFacilities, getThingsToDo } from "@/api/GepService";

interface ItemLike {
  _id?: unknown;
  itemCode?: unknown;
  activated?: unknown;
}

export interface ItemDeepLinkListItem {
  _id: string;
  itemCode: string | null;
  activated?: unknown;
}

export interface DeepLinkNavigationTarget {
  pageid: string;
  query: Record<string, string>;
}

export interface DeepLinkAuth {
  merchantId?: string;
  lang: string;
  token: string;
}

interface ResolveDeepLinkNavigationParams {
  to: string;
  activityId?: string | null;
  categoryId?: string | null;
  auth: DeepLinkAuth;
}

interface DeepLinkTarget {
  list: string;
  detail: string;
  hasCategory: boolean;
  fetchItems: (auth: DeepLinkAuth) => Promise<unknown>;
}

// ponytail: shared by facilities + things_to_do deeplinks; add entries here as
// other list/detail pairs get the same redirect.
const targetByTo: Record<string, DeepLinkTarget> = {
  facilities: {
    list: "facilities",
    detail: "facilities_detail",
    hasCategory: true,
    fetchItems: (auth) =>
      getFacilities({
        merchantId: auth.merchantId || "",
        lang: auth.lang,
        token: auth.token,
      }),
  },
  things_to_do: {
    list: "things_to_do",
    detail: "thingstodo_detail",
    hasCategory: false,
    fetchItems: (auth) => getThingsToDo({ lang: auth.lang, token: auth.token }),
  },
};

const normalizeString = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const normalizeComparableString = (value: unknown): string => {
  return normalizeString(value).toLowerCase();
};

const isArray = (value: unknown): value is ItemLike[] => {
  return Array.isArray(value);
};

const shouldIncludeItem = (item: ItemDeepLinkListItem): boolean => {
  return !(
    item.activated === false ||
    normalizeComparableString(item.activated) === "false"
  );
};

export function extractItemList(payload: unknown): ItemDeepLinkListItem[] {
  let list: ItemLike[] = [];

  if (isArray(payload)) {
    list = payload;
  } else if (payload && typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    if (isArray(record.results)) {
      list = record.results;
    } else if (
      record.results &&
      typeof record.results === "object" &&
      isArray((record.results as Record<string, unknown>).details)
    ) {
      list = (record.results as Record<string, unknown>).details as ItemLike[];
    } else if (isArray(record.details)) {
      list = record.details;
    }
  }

  return list
    .map((item) => {
      const id = normalizeString(item?._id);
      return {
        _id: id,
        itemCode:
          item?.itemCode === undefined || item?.itemCode === null
            ? null
            : String(item.itemCode),
        activated: item?.activated,
      };
    })
    .filter((item) => item._id.length > 0);
}

export function resolveItemId(
  activityId: string | null | undefined,
  items: ItemDeepLinkListItem[],
): string | null {
  const normalizedActivityId = normalizeComparableString(activityId);
  if (!normalizedActivityId) return null;

  const activeItems = items.filter(shouldIncludeItem);

  const idMatch = activeItems.find(
    (item) => normalizeComparableString(item._id) === normalizedActivityId,
  );
  if (idMatch) return idMatch._id;

  const codeMatch = activeItems.find(
    (item) => normalizeComparableString(item.itemCode) === normalizedActivityId,
  );
  if (codeMatch) return codeMatch._id;

  return null;
}

const buildTargetQuery = (
  target: DeepLinkTarget,
  lang: string,
  categoryId?: string | null,
): Record<string, string> => {
  const query: Record<string, string> = { lang };
  if (target.hasCategory) {
    const normalizedCategoryId = normalizeString(categoryId);
    if (normalizedCategoryId) {
      query.categoryId = normalizedCategoryId;
    }
  }
  return query;
};

export async function resolveDeepLinkNavigationTarget(
  params: ResolveDeepLinkNavigationParams,
): Promise<DeepLinkNavigationTarget> {
  const { to, activityId, categoryId, auth } = params;
  const target = targetByTo[to];
  const normalizedActivityId = normalizeString(activityId);

  if (!target) {
    return {
      pageid: to,
      query: { lang: auth.lang },
    };
  }

  const listQuery = buildTargetQuery(target, auth.lang, categoryId);

  if (!normalizedActivityId) {
    return {
      pageid: target.list,
      query: listQuery,
    };
  }

  try {
    const payload = await target.fetchItems(auth);
    const items = extractItemList(payload);
    const resolvedItemId = resolveItemId(normalizedActivityId, items);

    if (!resolvedItemId) {
      console.warn(
        `[item-deeplink] activityId "${normalizedActivityId}" did not match an active ${to} item`,
      );
      return {
        pageid: target.list,
        query: listQuery,
      };
    }

    return {
      pageid: target.detail,
      query: {
        ...listQuery,
        id: resolvedItemId,
      },
    };
  } catch (error) {
    console.warn("[item-deeplink] failed to fetch list", { error, to });
    return {
      pageid: target.list,
      query: listQuery,
    };
  }
}
